import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _defineProperty from "/usr/app/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import React from "react";
var __jsx = React.createElement;
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api__getShopApplyOrderV2, api__getOrderWithPaymentInfoV2 } from "@/apis/orders/orders";
import { useSession } from "@/_MiniApps/auth/AuthContext";
import useIsMobile from "@hooks/useIsMobile";
import { normalizeIdNumber } from "@/utils/stringHelpers";

/**
 * Dedicated context for the credit-card checkout flow.
 *
 * Intentionally separate from CheckoutOrderContext so the credit-card flow keeps
 * its OWN order/form/warranty state and persistence. This guarantees the warranty
 * (and city/name) selected in the cash (shop-apply) flow can never leak into the
 * credit-card flow and vice-versa — they no longer share any provider state or
 * sessionStorage key, and the credit-card mount always re-derives its order from
 * its own persisted selection.
 */

// credit-card owns its OWN persist key — never shared with the cash/shop-apply flow.
var getStorageKey = function getStorageKey(postId) {
  return "credit_card_form_data_".concat(postId);
};
var readSession = function readSession(postId) {
  try {
    if (typeof sessionStorage === "undefined") return {};
    var raw = sessionStorage.getItem(getStorageKey(postId));
    return raw ? JSON.parse(raw) : {};
  } catch (_unused) {
    return {};
  }
};
var extractQueryParams = function extractQueryParams() {
  var searchParams = new URLSearchParams(__IS_CSR__ ? window.location.search : "");
  var post_id = Number(searchParams.get("post_id")) || Number(searchParams.get("postId"));
  var order_id = normalizeIdNumber(searchParams.get("id"));
  if (!order_id) {
    order_id = normalizeIdNumber(searchParams.get("order_id"));
  }
  return {
    post_id: post_id,
    order_id: order_id
  };
};
var getFormDataDefault = function getFormDataDefault(user) {
  return {
    full_name: (user === null || user === void 0 ? void 0 : user.full_name) || "",
    city_id: null,
    phone_number: (user === null || user === void 0 ? void 0 : user.phone_number) || "",
    whatsapp_optin: 1,
    code: null
  };
};
var CreditCardCheckoutContext = /*#__PURE__*/createContext(null);
export function useCreditCardCheckout() {
  return useContext(CreditCardCheckoutContext);
}
export default function CreditCardCheckoutContextProvider(_ref) {
  var children = _ref.children;
  var location = useLocation();
  var _useSession = useSession(),
    user = _useSession.user;
  var isMobile = useIsMobile();
  var _useState = useState(extractQueryParams()),
    queryParams = _useState[0],
    setQueryParams = _useState[1];
  var _useState2 = useState(null),
    checkoutOrderV2 = _useState2[0],
    setCheckoutOrderV2 = _useState2[1];
  var _useState3 = useState(function () {
      return getFormDataDefault(user);
    }),
    formData = _useState3[0],
    setFormData = _useState3[1];
  var _useState4 = useState(false),
    fetch_loading = _useState4[0],
    setFetch_loading = _useState4[1];
  var _useState5 = useState(null),
    fetching_error = _useState5[0],
    setFetching_error = _useState5[1];
  var setFormItem = function setFormItem(key, value) {
    setFormData(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, key, value));
    });
  };
  var fetch__getOrderDetailsByParamsV2 = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(post_id, params, onSuccess) {
      var data, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            _context.p = 0;
            setFetch_loading(true);
            _context.n = 1;
            return api__getShopApplyOrderV2(post_id, params, isMobile);
          case 1:
            data = _context.v;
            setCheckoutOrderV2(data);
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data);
            _context.n = 3;
            break;
          case 2:
            _context.p = 2;
            _t = _context.v;
            setFetching_error(_t);
          case 3:
            _context.p = 3;
            setFetch_loading(false);
            return _context.f(3);
          case 4:
            return _context.a(2);
        }
      }, _callee, null, [[0, 2, 3, 4]]);
    }));
    return function fetch__getOrderDetailsByParamsV2(_x, _x2, _x3) {
      return _ref2.apply(this, arguments);
    };
  }();
  var fetch__getOrderWithPaymentInfoV2 = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2(post_id, params) {
      var data, _t2;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.p = _context2.n) {
          case 0:
            _context2.p = 0;
            setFetch_loading(true);
            _context2.n = 1;
            return api__getOrderWithPaymentInfoV2(post_id, params);
          case 1:
            data = _context2.v;
            setCheckoutOrderV2(data);
            _context2.n = 3;
            break;
          case 2:
            _context2.p = 2;
            _t2 = _context2.v;
            setFetching_error(_t2);
          case 3:
            _context2.p = 3;
            setFetch_loading(false);
            return _context2.f(3);
          case 4:
            return _context2.a(2);
        }
      }, _callee2, null, [[0, 2, 3, 4]]);
    }));
    return function fetch__getOrderWithPaymentInfoV2(_x4, _x5) {
      return _ref3.apply(this, arguments);
    };
  }();
  useEffect(function () {
    setQueryParams(extractQueryParams());
  }, [location]);

  // Mount restore: derive the order ONLY from the credit-card flow's own persisted
  // selection. When nothing is saved for this flow, explicitly send warranty_id=0 so
  // the backend does NOT fall back to a warranty cached from the cash flow.
  useEffect(function () {
    if (!(queryParams !== null && queryParams !== void 0 && queryParams.post_id)) return;
    var saved = readSession(queryParams.post_id);
    var savedCityId = saved.city_id ? Number(saved.city_id) : undefined;
    var savedWarrantyId = saved.warranty_id ? Number(saved.warranty_id) : undefined;
    if (saved.full_name != null) setFormItem("full_name", saved.full_name);
    if (savedCityId != null) setFormItem("city_id", savedCityId);
    if (savedWarrantyId != null) setFormItem("warranty_id", savedWarrantyId);
    fetch__getOrderDetailsByParamsV2(queryParams.post_id, {
      order_id: queryParams.order_id || undefined,
      city_id: savedCityId,
      warranty_id: savedWarrantyId != null ? savedWarrantyId : 0
    });
  }, [queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id, queryParams === null || queryParams === void 0 ? void 0 : queryParams.order_id]);
  var state = {
    checkoutOrderV2: checkoutOrderV2,
    formData: formData,
    fetch_loading: fetch_loading,
    fetching_error: fetching_error
  };
  var actions = {
    setFormItem: setFormItem
  };
  var remote_actions = {
    fetch__getOrderDetailsByParamsV2: fetch__getOrderDetailsByParamsV2,
    fetch__getOrderWithPaymentInfoV2: fetch__getOrderWithPaymentInfoV2
  };
  return /*#__PURE__*/React.createElement(CreditCardCheckoutContext.Provider, {
    value: {
      state: state,
      actions: actions,
      remote_actions: remote_actions,
      queryParams: queryParams
    }
  }, children);
}